import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import { coverStyleService, CoverStyle as CoverStyleType } from '../services/coverStyleService';
import { PageHeader } from '../components/PageHeader';
import { Palette } from 'lucide-react';
import { ErrorNotification } from '../components/ErrorNotification';

// Типы для контекстов, которые существуют в проекте

interface UserProfile {
  nickname: string;
  email: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  profile: UserProfile | null;
  login: (token: string) => void;
  logout: () => void;
}

// Компонент для создания/редактирования стиля
const StyleForm: React.FC<{
  style: Partial<CoverStyleType>;
  onSave: (style: Partial<CoverStyleType>) => void;
  onCancel: () => void;
}> = ({ style, onSave, onCancel }) => {
  const [name, setName] = useState(style.name || '');
  const [prompt, setPrompt] = useState(style.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(style.negativePrompt || '');
  const [modelStyle, setModelStyle] = useState(style.modelStyle || '');
  const [imageBase64, setImageBase64] = useState(style.imageBase64 || '');
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Загрузка изображения через API
  const handleUploadImage = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    
    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setIsUploading(true);
        
        try {
          // Конвертация файла в base64
          const base64Image = await coverStyleService.uploadImage(file);
          setImageBase64(base64Image);
          setFormError(null);
        } catch (error) {
          console.error('Ошибка при загрузке изображения:', error);
          setFormError('Не удалось загрузить изображение');
        } finally {
          setIsUploading(false);
        }
      }
    };
    
    fileInput.click();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Пожалуйста, введите название стиля');
      return;
    }
    if (!prompt.trim()) {
      setFormError('Пожалуйста, введите промпт для генерации');
      return;
    }
    if (!imageBase64) {
      setFormError('Пожалуйста, добавьте изображение');
      return;
    }

    setFormError(null);
    onSave({ 
      ...style,
      name, 
      prompt,
      negativePrompt,
      modelStyle,
      imageBase64
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      {formError && (
        <ErrorNotification
          message={formError}
          onClose={() => setFormError(null)}
        />
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Название стиля
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Введите название стиля"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Промпт для генерации
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Опишите свой стиль для ИИ-генерации"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Негативный промпт
        </label>
        <textarea
          value={negativePrompt}
          onChange={(e) => setNegativePrompt(e.target.value)}
          rows={3}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Опишите, что не должно присутствовать в изображении (low quality, bad anatomy, text и т.д.)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Пример изображения
        </label>
        {imageBase64 ? (
          <div className="relative">
            <img 
              src={imageBase64} 
              alt="Пример стиля" 
              className="w-full h-48 object-cover rounded-lg"
            />
            <button 
              type="button"
              onClick={() => setImageBase64('')}
              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        ) : (
          <button 
            type="button"
            onClick={handleUploadImage}
            disabled={isUploading}
            className="w-full h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isUploading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Загрузка...
              </div>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 mb-2">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
                <span className="text-gray-500 dark:text-gray-400">Загрузить изображение</span>
              </>
            )}
          </button>
        )}
      </div>

      <div className="flex justify-end space-x-4 pt-4">
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
        >
          Отмена
        </Button>
        <Button
          type="submit"
          variant="primary"
        >
          Сохранить
        </Button>
      </div>
    </form>
  );
};

// Основная страница управления стилями
const CoverStyles: React.FC = () => {
  const auth = useAuth() as AuthContextValue;
  
  const navigate = useNavigate();
  
  const [styles, setStyles] = useState<CoverStyleType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStyle, setCurrentStyle] = useState<Partial<CoverStyleType>>({});

  // Загрузка стилей пользователя
  useEffect(() => {
    const fetchStyles = async () => {
      setIsLoading(true);
      try {
        const fetchedStyles = await coverStyleService.getUserStyles();
        setStyles(fetchedStyles);
      } catch (error) {
        console.error('Ошибка при загрузке стилей:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      fetchStyles();
    }
  }, [auth]);

  // Обработчик для создания нового стиля
  const handleCreateStyle = () => {
    setCurrentStyle({});
    setIsEditing(true);
  };

  // Обработчик для редактирования стиля
  const handleEditStyle = (style: CoverStyleType) => {
    setCurrentStyle(style);
    setIsEditing(true);
  };

  // Обработчик для удаления стиля
  const handleDeleteStyle = async (styleId: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот стиль?')) {
      return;
    }

    try {
      await coverStyleService.deleteStyle(styleId);
      setStyles(prevStyles => prevStyles.filter(style => style.id !== styleId));
    } catch (error) {
      console.error('Ошибка при удалении стиля:', error);
    }
  };

  // Обработчик для сохранения стиля (создание или обновление)
  const handleSaveStyle = async (style: Partial<CoverStyleType>) => {
    try {
      if (style.id) {
        // Обновление существующего стиля
        const updatedStyle = await coverStyleService.updateStyle({
          id: style.id,
          name: style.name || '',
          prompt: style.prompt || '',
          negativePrompt: style.negativePrompt,
          modelStyle: style.modelStyle,
          imageBase64: style.imageBase64
        });
        
        setStyles(prevStyles => 
          prevStyles.map(s => 
            s.id === style.id ? updatedStyle : s
          )
        );
        
      } else {
        // Создание нового стиля
        const newStyle = await coverStyleService.createStyle({
          name: style.name || '',
          prompt: style.prompt || '',
          negativePrompt: style.negativePrompt,
          modelStyle: style.modelStyle,
          imageBase64: style.imageBase64
        });
        
        setStyles(prevStyles => [...prevStyles, newStyle]);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Ошибка при сохранении стиля:', error);
    }
  };

  // Обработчик для возврата к контент-плану
  const handleReturn = () => {
    // Проверяем, есть ли сохраненное состояние редактирования поста
    const pendingEdit = localStorage.getItem('pendingPostEdit');
    if (pendingEdit) {
      navigate('/articles/content-plan');
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding">
      <div className="hidden md:block">
        <NavigationMenu onBack={handleReturn} />
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Стили обложек"
          subtitle="Создавайте и управляйте стилями обложек для ваших проектов"
        />

        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            {isEditing ? (
              <Button
                onClick={() => setIsEditing(false)}
                variant="primary"
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-lg transform hover:scale-105 border-none"
              >
                К списку стилей
              </Button>
            ) : (
              <Button
                onClick={handleCreateStyle}
                variant="primary"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg transform hover:scale-105 border-none"
              >
                Создать новый стиль
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6">
              <StyleForm 
                style={currentStyle} 
                onSave={handleSaveStyle} 
                onCancel={() => setIsEditing(false)} 
              />
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              // Состояние загрузки
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 animate-pulse">
                  <div className="h-48 bg-gray-300 dark:bg-gray-700 rounded-lg mb-4"></div>
                  <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))
            ) : styles.length > 0 ? (
              // Отображение стилей
              styles.map(style => (
                <div key={style.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                  <div className="relative aspect-video">
                    <img 
                      src={style.imageBase64} 
                      alt={`Стиль ${style.name}`} 
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white">{style.name}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 text-sm line-clamp-2 mb-4">{style.prompt}</p>
                    <div className="flex justify-end space-x-2">
                      <Button
                        onClick={() => handleEditStyle(style)}
                        variant="primary"
                        isIcon
                        className="text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"></path>
                          <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"></polygon>
                        </svg>
                      </Button>
                      <Button
                        onClick={() => handleDeleteStyle(style.id)}
                        variant="danger"
                        isIcon
                        className="text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Нет стилей
              <div className="col-span-full text-center py-12">
                <div className="mx-auto w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">У вас пока нет созданных стилей</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">Создайте свой первый стиль для обложек, который будет использоваться в контент-плане</p>
                <Button
                  onClick={handleCreateStyle}
                  variant="primary"
                >
                  Создать стиль
                </Button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverStyles; 